import { useEffect, useState } from 'react'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { SignSheet } from '../components/SignSheet'
import { KV } from '../components/Controls'
import { useStore } from '../state/store'
import { readDay } from '../state/day'
import { closeDay, loadCloseData, reopenDay, setCloseTask, clockIn, clockOut, type DayClose, type OpenSale, type Punch } from '../lib/api'
import { fmtC, type Employee } from '../lib/types'
import { I } from '../components/Icons'
import { DoneMark } from '../components/DoneMark'
import { Working } from '../components/StarTick'

const METHOD = { cash: 'Cash', zelle: 'Zelle', crypto: 'Crypto', paypal: 'PayPal', card: 'Card in store (Shopify POS)', website: 'Website (starchive.la)' } as Record<string, string>
const ORDER = ['cash', 'zelle', 'crypto', 'paypal', 'card', 'website']
const tm = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })

/** Close out: the closing checklist (each task signed with the ID of whoever is signed in), and only the last person on
 *  the clock closes. Closing clocks the closer out; then lock the iPad. The drawer count is off (Tim, 29 Sep: never used;
 *  can come back later): the close is sent with skip_count and the float carries over unchanged. */
export function Close({ onLock }: { onLock: () => void }) {
  const nav = useNav()
  const { employees } = useStore()
  const me = readDay()
  const nameOf = (id: string) => employees.find((e) => e.employee_id === id)?.name ?? id
  const [data, setData] = useState<Awaited<ReturnType<typeof loadCloseData>> | null>(null)
  const [notes, setNotes] = useState('')
  const [sign, setSign] = useState<'reopen' | Punch | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState<DayClose | null>(null)
  const load = () => loadCloseData().then(setData).catch((e) => setErr((e as Error).message))
  useEffect(() => { load() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const today = data?.today ?? []
  // 'website' is card orders placed on starchive.la; 'card' is card taken in the store (counter or rung in POS)
  const key = (t: { payment_method: string; source: string }) => t.payment_method === 'card' ? (t.source === 'web' ? 'website' : 'card') : t.payment_method
  const tot = (m: string) => today.filter((t) => key(t) === m).reduce((a, t) => a + Number(t.total_cents), 0)
  const cnt = (m: string) => today.filter((t) => key(t) === m).reduce((a, t) => a + t.sales, 0)
  const all = today.reduce((a, t) => a + Number(t.total_cents), 0)
  const closed = done ?? data?.closed ?? null

  // checklist
  const tasks = data?.tasks ?? []
  const doneMap = new Map((data?.done ?? []).map((d) => [d.task_id, d]))
  const left = tasks.filter((t) => !doneMap.has(t.id)).length
  const toggle = async (id: string) => {
    if (!me || !data) return
    const was = doneMap.get(id)
    const next = was ? data.done.filter((d) => d.task_id !== id) : [...data.done, { task_id: id, employee_id: me.employee_id, done_at: new Date().toISOString() }]
    setData({ ...data, done: next }); setErr('')
    try { await setCloseTask(me.employee_id, id, !was) } catch (ex) { setErr((ex as Error).message); load() }
  }

  // who is still here
  const onClock = data?.onClock ?? []
  const meOn = onClock.find((p) => p.employee_id === me?.employee_id)
  const others = onClock.filter((p) => p.employee_id !== me?.employee_id)

  const blocker = !data ? <Working>Loading</Working> : left ? `Finish the checklist (${left} left)` : !meOn ? 'Clock in first' : others.length ? `Waiting for ${others.map((p) => p.name).join(', ')} to clock out` : ''

  const doClose = async () => {
    if (!me || blocker) return
    setBusy(true); setErr('')
    try {
      const r = await closeDay({ employee_id: me.employee_id, float_cents: data?.float_cents ?? 0, counted_cents: 0, first_count_cents: null, recounted: false, notes, skip_count: true })
      setDone(r)
    } catch (ex) { setErr((ex as Error).message); load() } finally { setBusy(false) }
  }
  const doReopen = async (e: Employee) => {
    setBusy(true); setErr('')
    try { await reopenDay(e.employee_id); setDone(null); setSign(null); await load() }
    catch (ex) { setErr((ex as Error).message) } finally { setBusy(false) }
  }
  const doOutFor = async (o: Employee) => {
    if (!sign || sign === 'reopen') return
    setBusy(true); setErr('')
    try { await clockOut(o.employee_id, sign.employee_id); setSign(null); await load() } catch (ex) { setErr((ex as Error).message) } finally { setBusy(false) }
  }
  const doIn = async () => {
    if (!me) return
    setBusy(true); setErr('')
    try { await clockIn(me.employee_id); await load() } catch (ex) { setErr((ex as Error).message) } finally { setBusy(false) }
  }
  const dateLabel = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric' })

  if (closed) {
    return (
      <Page bar={<TopBar back="Home" backTo="/" title="Closed out" caption={dateLabel} />}
        aside={<>
          <div className="aside__body"><div className="icard-head"><b>Closed</b><span>by {nameOf(closed.closed_by)} · {tm(closed.closed_at)}</span></div>
            {closed.notes ? <div className="hint" style={{ padding: '10px 24px' }}>{closed.notes}</div> : <div className="hint" style={{ padding: '10px 24px' }}>No note left.</div>}
          </div>
          <div className="aside__foot" style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn-secondary" style={{ flex: 1 }} onClick={() => setSign('reopen')}>Reopen (owner)</button>
            <button type="button" className="cta cta--center" style={{ flex: 1 }} onClick={onLock}>Lock the iPad</button>
          </div>
        </>}>
        <div className={'done' + (closed.checklist?.length ? ' done--long' : '')}><DoneMark /><div className="done__title">Closed out</div>
          <div className="done__sub">{fmtC(Object.values(closed.totals).reduce((a, t) => a + t.total_cents, 0))} in sales · {closed.intakes} intakes · {closed.open_sales} still open · {nameOf(closed.closed_by)} clocked out</div>
          {!!closed.checklist?.length && <div className="card review-rows" style={{ width: 520, marginTop: 20 }}>
            {closed.checklist.map((c) => <KV key={c.id} k={c.title} v={`${c.name || c.employee_id} · ${tm(c.done_at)}`} />)}
          </div>}
          <div className="card review-rows" style={{ width: 520, marginTop: 12 }}>
            {ORDER.filter((m) => closed.totals[m]).map((m) => <KV key={m} k={METHOD[m]} v={`${closed.totals[m].sales} · ${fmtC(closed.totals[m].total_cents)}`} />)}
          </div>
          {err && <div className="banner" style={{ background: '#d70015', marginTop: 12 }}>{err}</div>}
        </div>
        {sign === 'reopen' && <SignSheet title="Owner ID to reopen the day" ownerOnly onConfirm={doReopen} onClose={() => setSign(null)} busy={busy} />}
      </Page>
    )
  }

  return (
    <Page bar={<TopBar back="Home" backTo="/" title="Close out" caption={dateLabel} />}
      aside={<>
        <div className="aside__body">
          <div className="icard-head"><b>Close out</b><span>{data ? `Closing as ${me?.name ?? ''}` : 'Loading…'}</span></div>
          <div className="card" style={{ margin: '0 20px' }}>
            <div className="row fieldrow"><label htmlFor="cnotes" className="row__label">Note</label><input id="cnotes" className="row__input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything low, broken or left undone (optional)" /></div>
          </div>
          {err && <div className="banner" style={{ margin: '12px 20px 0', background: '#d70015' }}>{err}</div>}
        </div>
        <div className="aside__foot">
          <button type="button" className="cta" disabled={!!blocker || busy} onClick={doClose}><span>{busy ? <Working>Closing</Working> : blocker || 'Close out and clock out'}</span><span /></button>
        </div>
      </>}>
      <div className="section-title">Closing checklist · <span key={left} className="m-fadein">{tasks.length - left} of {tasks.length}</span> done</div>
      <div className="ck-progress"><span style={{ width: tasks.length ? `${((tasks.length - left) / tasks.length) * 100}%` : '0%' }} /></div>
      <div className="card">
        {tasks.map((t, i) => {
          const d = doneMap.get(t.id)
          return (
            <button key={t.id} type="button" className={'ck' + (d ? ' ck--done' : '')} onClick={() => toggle(t.id)} aria-pressed={!!d}>
              <span className="ck__box">{d ? I.tick(18) : <span style={{ color: '#8e8e93', fontSize: 13, fontWeight: 700 }}>{i + 1}</span>}</span>
              <span><span className="ck__title">{t.title}</span>{t.desc && <span className="ck__desc" style={{ display: 'block' }}>{t.desc}</span>}</span>
              <span className="ck__by">{d ? `${nameOf(d.employee_id)} · ${tm(d.done_at)}` : 'Tap when done'}</span>
            </button>
          )
        })}
        {data && tasks.length === 0 && <div className="empty">No checklist set.</div>}
      </div>
      <div className="hint">Each tick is signed with the ID signed in on this iPad. Tap again to undo.</div>

      <div className="section-title">Who's still here</div>
      <div className="card">
        {onClock.map((p) => (
          <div key={p.id} className="tc-row">
            <span><span className="tc-row__name">{p.name}{p.employee_id === me?.employee_id ? ' (you)' : ''}</span><span className="tc-row__sub" style={{ display: 'block' }}>On the clock since {tm(p.clock_in)}</span></span>
            {p.employee_id !== me?.employee_id ? <button type="button" className="btn-secondary" onClick={() => setSign(p)}>Clock out (owner)</button> : <span />}
            <span />
          </div>
        ))}
        {data && onClock.length === 0 && <div className="empty">Nobody is on the clock.</div>}
      </div>
      {data && !meOn && <div className="banner" style={{ background: '#ff9500', marginTop: 10 }}>You're not on the clock. Only the last person at the showroom closes out. <button type="button" className="btn-secondary" disabled={busy} onClick={doIn} style={{ marginLeft: 8 }}>Clock in</button></div>}
      {others.length > 0 && <div className="hint">Only the last person at the showroom closes out. {others.map((p) => p.name).join(', ')} {others.length === 1 ? 'clocks' : 'clock'} out on the Time clock before leaving (an owner can clock someone out who forgot).</div>}

      <div className="section-title">Sales today</div>
      <div className="card review-rows">
        {ORDER.map((m) => <KV key={m} k={METHOD[m]} v={cnt(m) ? `${cnt(m)} ${cnt(m) === 1 ? 'sale' : 'sales'} · ${fmtC(tot(m))}` : '—'} />)}
        <KV k="Total" v={fmtC(all)} bold />
      </div>
      <div className="hint">Paid sales only. Card sales are what Shopify POS took today, matched to the counter.</div>

      <div className="section-title">Still open</div>
      <div className="card">
        {(data?.open ?? []).map((s: OpenSale) => (
          <button key={s.id} type="button" className="person" onClick={() => nav('/sales')}>
            <span className="person__initial" style={{ borderRadius: 10, background: '#ff9500' }}>!</span>
            <span style={{ flexGrow: 1, minWidth: 0 }}><span className="person__name">Sale #{s.sale_no} · {fmtC(s.total_cents)}</span><span className="person__sub">{s.status === 'awaiting_card' ? 'Waiting for the card in POS' : `Waiting for ${METHOD[s.payment_method] ?? s.payment_method}`}{s.customer ? ` · ${s.customer}` : ''} · ID {s.employee_id}</span></span>
            <span className="hint">Collect or cancel ›</span>
          </button>
        ))}
        {data && data.open.length === 0 && <div className="empty">Nothing waiting. Every sale today is paid or cancelled.</div>}
      </div>
      {data && data.open.length > 0 && <div className="hint">Open sales are fine to leave; they stay on Today's sales. Cancel the ones that fell through so the pieces read as available.</div>}

      <div className="section-title">Intake today</div>
      <div className="card review-rows">
        <KV k="Pieces taken in" v={String(data?.intakes ?? 0)} />
        <KV k={`Paid for bought pieces${data?.payouts.bought_pieces ? ` (${data.payouts.bought_pieces})` : ''}`} v={fmtC(data?.payouts.bought_cents ?? 0)} />
        {data && (['cash', 'zelle', 'wire', 'crypto'] as const).filter((m) => data.payouts[m] > 0).map((m) => (
          <KV key={m} k={m === 'cash' ? '· by cash' : `· by ${{ zelle: 'Zelle', wire: 'wire', crypto: 'crypto' }[m]}`} v={fmtC(data.payouts[m])} />
        ))}
      </div>
      {sign === 'reopen' && <SignSheet title="Owner ID to reopen the day" ownerOnly onConfirm={doReopen} onClose={() => setSign(null)} busy={busy} />}
      {sign && sign !== 'reopen' && <SignSheet title={`Owner ID to clock ${sign.name} out`} ownerOnly onConfirm={doOutFor} onClose={() => setSign(null)} busy={busy} />}
    </Page>
  )
}
